import { IPromotionDetail } from 'core';

export type PromotionDetailTab = 'details' | 'entries' | 'conditions';
export type PromotionDetailTabContentType = 'details' | 'entries' | 'conditions' | 'claim_instructions';

export interface IPromotionDetailCtaState {
  labelKey: string;
  visible: boolean;
  disabled: boolean;
  iconName: 'trophy' | null;
}

export interface IPromotionDetailState {
  cta: IPromotionDetailCtaState;
  heroMessageKey: string;
  statusLabelKey: string;
  activeTabContentType: PromotionDetailTabContentType;
  isLost: boolean;
  isWinner: boolean;
  isClaimState: boolean;
  isParticipating: boolean;
  isMaxEntriesUsed: boolean;
  isRewardLadder: boolean;
}

export function getPromotionDetailState(
  promotion: IPromotionDetail,
  selectedTab: PromotionDetailTab
): IPromotionDetailState {
  const isLost = promotion.result_state === 'lost';
  const isWonUnclaimed = promotion.result_state === 'won_unclaimed';
  const isWonClaimed = promotion.result_state === 'won_claimed';
  const isWinner = isWonUnclaimed || isWonClaimed;
  const isClaimState = isWinner;
  const isParticipating = promotion.participation_state === 'participating';
  const isMaxEntriesUsed = promotion.participation_state === 'max_entries_used';
  const isRewardLadder = promotion.promotion_kind === 'prize_ladder';

  return {
    cta: getPromotionDetailCtaState(promotion),
    heroMessageKey: getPromotionDetailHeroMessageKey(promotion),
    statusLabelKey: getPromotionDetailStatusLabelKey(promotion),
    activeTabContentType: getPromotionDetailActiveTabContentType(promotion, selectedTab),
    isLost,
    isWinner,
    isClaimState,
    isParticipating,
    isMaxEntriesUsed,
    isRewardLadder
  };
}

export function getPromotionDetailCtaState(promotion: IPromotionDetail): IPromotionDetailCtaState {
  if (promotion.promotion_kind === 'instant_reward' && promotion.result_state === 'active') {
    return { labelKey: 'promotions.detail.instantReward.tapHere', visible: false, disabled: false, iconName: null };
  }

  if (promotion.promotion_kind === 'prize_ladder' && promotion.result_state === 'active') {
    return { labelKey: getEnterLabelKey(promotion), visible: false, disabled: true, iconName: null };
  }

  if (promotion.result_state === 'won_unclaimed') {
    return { labelKey: 'promotions.detail.claimReward', visible: true, disabled: false, iconName: 'trophy' };
  }

  if (promotion.result_state === 'won_claimed') {
    return { labelKey: 'promotions.detail.rewardClaimedCta', visible: true, disabled: true, iconName: 'trophy' };
  }

  if (promotion.result_state === 'lost') {
    return { labelKey: getEnterLabelKey(promotion), visible: false, disabled: true, iconName: null };
  }

  if (promotion.participation_state === 'max_entries_used') {
    return { labelKey: 'promotions.detail.buyMoreEntries', visible: false, disabled: true, iconName: null };
  }

  if (promotion.participation_state === 'participating') {
    return { labelKey: 'promotions.detail.buyMoreEntries', visible: false, disabled: true, iconName: null };
  }

  return { labelKey: 'promotions.detail.applyFreeEntry', visible: false, disabled: true, iconName: null };
}

export function getPromotionDetailHeroMessageKey(promotion: IPromotionDetail): string {
  if (promotion.result_state === 'lost') {
    return 'promotions.detail.hero.lost';
  }

  if (promotion.result_state === 'won_unclaimed') {
    return 'promotions.detail.hero.wonUnclaimed';
  }

  if (promotion.result_state === 'won_claimed') {
    return 'promotions.detail.hero.wonClaimed';
  }

  if (promotion.participation_state === 'max_entries_used') {
    return 'promotions.detail.hero.maxEntriesUsed';
  }

  if (promotion.participation_state === 'participating') {
    return 'promotions.detail.hero.participating';
  }

  return 'promotions.detail.hero.active';
}

export function getPromotionDetailStatusLabelKey(promotion: IPromotionDetail): string {
  if (promotion.result_state === 'lost') {
    return 'promotions.detail.statusTags.ended';
  }

  if (promotion.result_state === 'won_unclaimed') {
    return 'promotions.detail.statusTags.unclaimed';
  }

  if (promotion.result_state === 'won_claimed') {
    return 'promotions.detail.statusTags.claimed';
  }

  if (promotion.participation_state === 'participating' || promotion.participation_state === 'max_entries_used') {
    return 'promotions.detail.statusTags.participating';
  }

  return 'promotions.detail.statusTags.active';
}

export function getPromotionDetailActiveTabContentType(
  promotion: IPromotionDetail,
  selectedTab: PromotionDetailTab
): PromotionDetailTabContentType {
  if (selectedTab === 'entries') {
    return 'entries';
  }

  if (selectedTab === 'conditions') {
    return promotion.result_state === 'won_unclaimed' || promotion.result_state === 'won_claimed'
      ? 'claim_instructions'
      : 'conditions';
  }

  return 'details';
}

function getEnterLabelKey(promotion: IPromotionDetail): string {
  return promotion.tickets === 1 ? 'promotions.detail.enterForTicket' : 'promotions.detail.enterForTicket_other';
}
